import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Navbar } from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import { useProducts } from "../../context/ProductContext";
import { useCart } from "../../context/CartContext";
import "./ProductDetail.css";

const ProductDetail = () => {
	const { slug } = useParams();
	const { getProductBySlug, getProductsByCategory, loading } = useProducts();
	const product = getProductBySlug(slug);
	const { addItem } = useCart();
	const [selectedImage, setSelectedImage] = useState(0);
	const [quantity, setQuantity] = useState(1);
	const [added, setAdded] = useState(false);

	const related = product
		? getProductsByCategory(product.category).filter((p) => p.id !== product.id).slice(0, 4)
		: [];

	useEffect(() => {
		window.scrollTo({ top: 0 });
		setSelectedImage(0);
		setQuantity(1);
		setAdded(false);
	}, [slug]);

	if (loading) {
		return (
			<>
				<Navbar />
				<div className="product-not-found">
					<div className="collection-spinner" />
					<p>Loading...</p>
				</div>
				<Footer />
			</>
		);
	}

	if (!product) {
		return (
			<>
				<Navbar />
				<div className="product-not-found">
					<h2>Product not found</h2>
					<Link to="/collections" className="back-to-shop">Return to Shop</Link>
				</div>
				<Footer />
			</>
		);
	}

	const handleAddToCart = () => {
		addItem(product, quantity);
		setAdded(true);
		setTimeout(() => setAdded(false), 2000);
	};

	return (
		<>
			<Helmet>
				<title>{product.title} - Aire Studio</title>
				<meta name="description" content={product.description} />
			</Helmet>

			<Navbar />

			<div className="product-detail-page">
				<nav className="product-breadcrumb">
					<div className="container">
						<Link to="/">Home</Link>
						<span className="breadcrumb-sep">/</span>
						<Link to={`/collections/${product.category}`}>{product.categoryLabel}</Link>
						<span className="breadcrumb-sep">/</span>
						<span className="breadcrumb-current">{product.title}</span>
					</div>
				</nav>

				<section className="product-detail section-padding">
					<div className="container">
						<div className="product-layout">
							<motion.div
								className="product-gallery"
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ duration: 0.6 }}
							>
								<div className="gallery-main">
									<img
										src={product.images[selectedImage]}
										alt={product.title}
										className="gallery-main-img"
									/>
								</div>
								{product.images.length > 1 && (
									<div className="gallery-thumbs">
										{product.images.map((img, i) => (
											<button
												key={i}
												className={`gallery-thumb ${i === selectedImage ? "active" : ""}`}
												onClick={() => setSelectedImage(i)}
											>
												<img src={img} alt={`${product.title} ${i + 1}`} />
											</button>
										))}
									</div>
								)}
							</motion.div>

							<motion.div
								className="product-info"
								initial={{ opacity: 0, x: 20 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ duration: 0.6, delay: 0.15 }}
							>
								<p className="product-category-label">{product.categoryLabel}</p>
								<h1 className="product-title">{product.title}</h1>
								<p className="product-price">${product.price.toFixed(2)}</p>
								<p className="product-shipping-info">Shipping calculated at checkout.</p>

								{product.description && (
									<div className="product-description">
										<p>{product.description}</p>
									</div>
								)}

								{product.details && product.details.length > 0 && (
									<ul className="product-details-list">
										{product.details.map((detail, i) => (
											<li key={i}>{detail}</li>
										))}
									</ul>
								)}

								<div className="product-quantity-row">
									<div className="product-quantity">
										<button
											className="pqty-btn"
											onClick={() => setQuantity(Math.max(1, quantity - 1))}
										>
											-
										</button>
										<span className="pqty-value">{quantity}</span>
										<button
											className="pqty-btn"
											onClick={() => setQuantity(quantity + 1)}
										>
											+
										</button>
									</div>
								</div>

								<button
									className={`add-to-cart-btn ${added ? "added" : ""}`}
									onClick={handleAddToCart}
								>
									{added ? "Added to Cart" : "Add to Cart"}
								</button>
							</motion.div>
						</div>
					</div>
				</section>

				{related.length > 0 && (
					<section className="related-products section-padding">
						<div className="container">
							<h3 className="related-title">You may also like</h3>
							<div className="related-grid">
								{related.map((item) => (
									<Link
										key={item.id}
										to={`/product/${item.slug}`}
										className="related-card"
									>
										<div className="related-card-image">
											<img src={item.images[0]} alt={item.title} />
										</div>
										<h4 className="related-card-title">{item.title}</h4>
										<p className="related-card-price">${item.price.toFixed(2)}</p>
									</Link>
								))}
							</div>
						</div>
					</section>
				)}
			</div>

			<Footer />
		</>
	);
};

export default ProductDetail;
